import {
  Component,
  signal,
  computed,
  inject,
  ViewChild,
  ViewChildren,
  QueryList,
  ElementRef,
  HostListener,
  OnInit,
  OnDestroy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { LucideAngularModule } from 'lucide-angular';
import { DashboardService } from '../../../features/dashboard/services/dashboard.service';
import { ProjectsDataDto, DashboardActiveRequestItemDto, DashboardAwaitingItemDto } from '../../models/dashboard.dto';
import { RecentItemsService, RecentSearchItem } from '../../services/recent-items.service';

type SearchTab = 'recent' | 'all-projects' | 'requests-assigned' | 'pending-approvals';

// Flat item model — optional fields are type-specific
interface FlatItem {
  type: 'section' | 'nav' | 'project' | 'sub-action' | 'recent' | 'active-request' | 'pending-approval';
  id: string;
  label: string;
  icon?: string;
  route?: string;
  queryParams?: Record<string, string>;
  // project / recent items
  color?: string;
  description?: string;
  projectId?: string;
  // section items
  count?: number;
  // request rows
  subtitle?: string;
  status?: string;
  statusBadgeClass?: string;
  // disabled nav entries ("Coming Soon")
  disabled?: boolean;
  disabledTag?: string;
}

interface StaticNavEntry {
  id: string;
  label: string;
  icon: string;
  route: string;
  disabled?: boolean;
  disabledTag?: string;
}

const NAV_ENTRIES: StaticNavEntry[] = [
  { id: 'nav-dashboard', label: 'Dashboard',     icon: 'layout-dashboard', route: '/dashboard' },
  { id: 'nav-requests',  label: 'Requests',      icon: 'clipboard-list',   route: '/requests'  },
  { id: 'nav-sites',     label: 'Sites Updates', icon: 'globe',            route: '/sites'     },
  { id: 'nav-workflows', label: 'Workflows',     icon: 'workflow',         route: '',           disabled: true, disabledTag: 'Coming Soon' },
  { id: 'nav-aeo',       label: 'AEO',           icon: 'message-circle',  route: '',           disabled: true, disabledTag: 'Coming Soon' },
];

const PROJECT_SUB_ACTIONS = [
  { key: 'requests', label: 'Requests', icon: 'clipboard-list', route: '/requests' },
  { key: 'sites',    label: 'Sites',    icon: 'layers',         route: '/sites'    },
] as const;

const TAB_DEFS: { id: SearchTab; label: string }[] = [
  { id: 'recent',              label: 'Recent' },
  { id: 'all-projects',        label: 'All Projects' },
  { id: 'requests-assigned',   label: 'Requests Assigned' },
  { id: 'pending-approvals',   label: 'Pending Approvals' },
];

@Component({
  selector: 'project-search',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './project-search.component.html',
  styleUrls: ['./project-search.component.scss']
})
export class ProjectSearchComponent implements OnInit, OnDestroy {
  @ViewChild('searchInput')   private inputRef!:   ElementRef<HTMLInputElement>;
  @ViewChildren('searchItem') private itemRefs!:   QueryList<ElementRef<HTMLButtonElement>>;

  private readonly dashboardService  = inject(DashboardService);
  private readonly recentItemsService = inject(RecentItemsService);
  private readonly router            = inject(Router);

  readonly tabDefs     = TAB_DEFS;
  readonly isOpen      = signal(false);
  readonly searchQuery = signal('');
  readonly activeIndex = signal(-1);
  readonly isLoading   = signal(false);
  readonly activeTab   = signal<SearchTab>('all-projects');

  private readonly allProjects       = signal<ProjectsDataDto[]>([]);
  private readonly activeRequests    = signal<DashboardActiveRequestItemDto[]>([]);
  private readonly awaitingApproval  = signal<DashboardAwaitingItemDto[]>([]);
  private refreshSub?: Subscription;

  // ── Per-tab flat lists (all share the same FlatItem shape driving the template + keyboard nav) ──

  private readonly recentFlatItems = computed<FlatItem[]>(() => {
    const q = this.searchQuery().toLowerCase().trim();
    const items = this.recentItemsService.items()
      .filter(r => !q || r.label.toLowerCase().includes(q))
      .map<FlatItem>(r => ({
        type: 'recent', id: `recent-${r.id}`, label: r.label,
        icon: r.icon, color: r.color, description: r.subtitle,
        route: r.route, queryParams: r.queryParams,
      }));

    if (items.length === 0) return [];
    return [{ type: 'section', id: 'sec-recent', label: 'Recent', count: items.length }, ...items];
  });

  private readonly allProjectsFlatItems = computed<FlatItem[]>(() => {
    const q    = this.searchQuery().toLowerCase().trim();
    const items: FlatItem[] = [];

    // ── Navigation shortcuts ─────────────────────────────────────────────
    const navItems = NAV_ENTRIES
      .filter(e => !q || e.label.toLowerCase().includes(q))
      .map<FlatItem>(e => ({
        type: 'nav', id: e.id, label: e.label, icon: e.icon, route: e.route,
        disabled: e.disabled, disabledTag: e.disabledTag,
      }));

    if (navItems.length > 0) {
      items.push({ type: 'section', id: 'sec-nav', label: 'Jump to' });
      items.push(...navItems);
    }

    // ── Projects (+ sub-actions when querying) ───────────────────────────
    const matched = q
      ? this.allProjects().filter(p =>
          p.name.toLowerCase().includes(q) ||
          (p.description?.toLowerCase().includes(q) ?? false))
      : this.allProjects();

    if (matched.length > 0) {
      items.push({
        type: 'section', id: 'sec-projects',
        label: q ? 'Projects' : 'All Projects',
        count: matched.length,
      });

      for (const project of matched) {
        items.push({
          type: 'project', id: `proj-${project.id}`,
          label: project.name, description: project.description,
          color: project.color, projectId: project.id,
          route: '/dashboard', queryParams: { projectId: project.id },
        });

        // Sub-actions only when user is actively searching
        if (q) {
          for (const sub of PROJECT_SUB_ACTIONS) {
            items.push({
              type: 'sub-action', id: `sub-${project.id}-${sub.key}`,
              label: sub.label, icon: sub.icon,
              route: sub.route, queryParams: { projectId: project.id },
              color: project.color,
            });
          }
        }
      }
    }

    return items;
  });

  private readonly requestsAssignedFlatItems = computed<FlatItem[]>(() => {
    const q = this.searchQuery().toLowerCase().trim();
    const matched = this.activeRequests().filter(r =>
      !q ||
      r.request_name.toLowerCase().includes(q) ||
      r.project_name.toLowerCase().includes(q));

    if (matched.length === 0) return [];

    const items: FlatItem[] = [
      { type: 'section', id: 'sec-requests-assigned', label: 'Requests Assigned', count: matched.length },
    ];
    for (const r of matched) {
      items.push({
        type: 'active-request', id: `req-${r.id}`, label: r.request_name,
        subtitle: `${r.project_name} / ${r.request_type}`,
        status: r.status_name, statusBadgeClass: this.getStatusBadgeClass(r.status_name),
        route: '/requests', queryParams: this.requestQueryParams(r.id, r.project_name),
      });
    }
    return items;
  });

  private readonly pendingApprovalsFlatItems = computed<FlatItem[]>(() => {
    const q = this.searchQuery().toLowerCase().trim();
    const matched = this.awaitingApproval().filter(r =>
      !q ||
      r.request_name.toLowerCase().includes(q) ||
      r.project_name.toLowerCase().includes(q));

    if (matched.length === 0) return [];

    const items: FlatItem[] = [
      { type: 'section', id: 'sec-pending-approvals', label: 'Pending Approvals', count: matched.length },
    ];
    for (const r of matched) {
      items.push({
        type: 'pending-approval', id: `awaiting-${r.id}`, label: r.request_name,
        subtitle: `${r.project_name} / ${r.request_type}`,
        route: '/requests', queryParams: this.requestQueryParams(r.id, r.project_name),
      });
    }
    return items;
  });

  readonly filteredFlatItems = computed<FlatItem[]>(() => {
    switch (this.activeTab()) {
      case 'recent':             return this.recentFlatItems();
      case 'requests-assigned':  return this.requestsAssignedFlatItems();
      case 'pending-approvals':  return this.pendingApprovalsFlatItems();
      case 'all-projects':
      default:                   return this.allProjectsFlatItems();
    }
  });

  readonly hasResults = computed(() =>
    this.filteredFlatItems().some(i => i.type !== 'section')
  );

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.loadDashboard();
    this.refreshSub = this.dashboardService.refreshDashboard$.subscribe(() => {
      this.loadDashboard(true);
    });
  }

  ngOnDestroy(): void {
    this.refreshSub?.unsubscribe();
  }

  private loadDashboard(forceRefresh = false): void {
    const cached = this.dashboardService.getCachedDashboardData();
    if (cached && !forceRefresh) {
      this.applyDashboardData(cached);
      return;
    }
    this.isLoading.set(true);
    this.dashboardService.getDashboardData().subscribe({
      next:  res => { this.applyDashboardData(res.data); this.isLoading.set(false); },
      error: ()  => this.isLoading.set(false),
    });
  }

  private applyDashboardData(data: { projects?: ProjectsDataDto[]; active_requests?: DashboardActiveRequestItemDto[]; awaiting_approval?: DashboardAwaitingItemDto[] }): void {
    this.allProjects.set(data.projects ?? []);
    this.activeRequests.set(data.active_requests ?? []);
    this.awaitingApproval.set(data.awaiting_approval ?? []);
  }

  /**
   * The dashboard's active-request/awaiting-approval DTOs only carry `project_name`, not a
   * project id, but the requests page needs `projectId` in the URL to load the right project's
   * list before it can deep-link into `requestId`. Resolve the id from the already-loaded
   * projects list by exact name match; omit it (best-effort navigation) if no match is found.
   */
  private requestQueryParams(requestId: string, projectName: string): Record<string, string> {
    const project = this.allProjects().find(p => p.name === projectName);
    return project ? { projectId: project.id, requestId } : { requestId };
  }

  private getStatusBadgeClass(statusName: string): string {
    const name = (statusName || '').toLowerCase();
    if (name === 'completed') return 'badge-completed';
    if (name === 'in progress') return 'badge-in-progress';
    if (name === 'to do') return 'badge-to-do';
    if (name === 'on hold') return 'badge-on-hold';
    if (name.includes('approval')) return 'badge-approval';
    return 'badge-default';
  }

  // ── Panel open / close ────────────────────────────────────────────────────

  open(): void {
    if (this.isOpen()) return;
    this.isOpen.set(true);
    this.activeIndex.set(-1);
    this.activeTab.set(this.recentItemsService.items().length > 0 ? 'recent' : 'all-projects');
    setTimeout(() => this.inputRef?.nativeElement.focus(), 0);
  }

  close(): void {
    this.isOpen.set(false);
    this.searchQuery.set('');
    this.activeIndex.set(-1);
    this.inputRef?.nativeElement.blur();
  }

  selectTab(tab: SearchTab): void {
    this.activeTab.set(tab);
    this.activeIndex.set(-1);
  }

  // ── Input events ──────────────────────────────────────────────────────────

  onInput(event: Event): void {
    this.searchQuery.set((event.target as HTMLInputElement).value);
    this.activeIndex.set(-1);
  }

  onKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveActive(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveActive(-1);
        break;
      case 'Enter':
        event.preventDefault();
        const idx = this.activeIndex();
        if (idx >= 0) this.selectItem(this.filteredFlatItems()[idx]);
        break;
      case 'Escape':
        this.close();
        break;
    }
  }

  // ── Keyboard navigation helpers ───────────────────────────────────────────

  private moveActive(dir: 1 | -1): void {
    const next = this.adjacentSelectableIndex(this.activeIndex(), dir);
    this.activeIndex.set(next);
    this.scrollActiveIntoView(next);
  }

  private adjacentSelectableIndex(from: number, dir: 1 | -1): number {
    const items = this.filteredFlatItems();
    let idx = from === -1 && dir === 1 ? 0 : from + dir;
    while (idx >= 0 && idx < items.length) {
      if (items[idx].type !== 'section' && !items[idx].disabled) return idx;
      idx += dir;
    }
    return from;
  }

  private scrollActiveIntoView(flatIdx: number): void {
    if (flatIdx < 0) return;
    const items = this.filteredFlatItems();
    // Map flat index → button index (sections + disabled entries have no DOM button)
    let btnIdx = 0;
    for (let i = 0; i < flatIdx; i++) {
      if (items[i].type !== 'section' && !items[i].disabled) btnIdx++;
    }
    setTimeout(() => {
      this.itemRefs?.toArray()[btnIdx]?.nativeElement
        .scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 0);
  }

  // ── Selection ─────────────────────────────────────────────────────────────

  selectItem(item: FlatItem): void {
    if (item.type === 'section' || item.disabled) return;

    if (item.type === 'project') {
      this.router.navigate(['/dashboard'], { queryParams: { projectId: item.projectId } });
    } else {
      // nav / sub-action / recent / active-request / pending-approval all carry route + queryParams
      this.router.navigate([item.route!], { queryParams: item.queryParams });
    }

    this.recordRecent(item);
    this.close();
  }

  private recordRecent(item: FlatItem): void {
    if (item.type === 'recent' || !item.route) return;

    const recentItem: Omit<RecentSearchItem, 'visitedAt'> = {
      id: item.id,
      type: item.type as RecentSearchItem['type'],
      label: item.label,
      subtitle: item.description ?? item.subtitle,
      icon: item.icon,
      color: item.color,
      route: item.route,
      queryParams: item.queryParams,
    };
    this.recentItemsService.record(recentItem);
  }

  // ── Global shortcut + Escape ──────────────────────────────────────────────

  @HostListener('document:keydown', ['$event'])
  onGlobalKeyDown(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      this.isOpen() ? this.close() : this.open();
      return;
    }
    if (event.key === 'Escape' && this.isOpen()) {
      this.close();
    }
  }
}
